import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled, { withTheme } from 'styled-components';
import { Table } from 'common/images/FaIcons';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { validateAndSwitchAccountWithCallback } from 'home/AssetHierarchy/actions/assetDetails.actions';
import { getTranslatedLabel } from 'home/Widget/actions/widget.actions';
import { FlexContainer } from '../components/atoms';
import Modal from '../components/organisms/Modal';
import BigNumberTable from './BigNumberTable';

const ChartContainer = styled(FlexContainer).attrs({
  alignItems: 'center',
  direction: 'column'
})`
  height: 100%;
  position: relative;
`;

const TITLE_HEIGHT = 40;
const TitleContainer = styled(FlexContainer).attrs({
  alignItems: 'center',
  justifyContent: 'space-between'
})`
  width: 100%;
  padding: 1em 0 0 1em;
  height: ${TITLE_HEIGHT}px;
`;

const Title = styled.div`
  color: ${props => props.theme.colors.darkGray};
  font-weight: 600;
`;

const TableButton = styled.button`
  background: none;
  border: 1px solid ${props => props.theme.colors.greyL};
  border-radius: 4px;
  width: 24px;
  height: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1em;
  color: ${props => props.theme.colors.greyXD};
  
  &:hover {
    background-color: ${props => props.theme.colors.greyXXL};
    color: ${props => props.theme.colors.greyXXD};
  }
`;

const NumberContainer = styled(FlexContainer).attrs({
  direction: 'column',
  alignItems: 'center',
  justifyContent: 'center',
})`
  color: ${props => props.color};
  height: 100%;

  ${props => props.isInteractive && `
    cursor: pointer;
  `}

  .big-number {
    ${props => props.theme.fonts.boldLabel}
    height: 60px;
    font-size: ${props => props.numberSize}px;
    padding: ${props => props.numberPadding}px;
    margin: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .number-label {
    ${props => props.theme.fonts.boldLabel}
    font-size: ${props => props.labelSize}px;
    text-align: center;
  }
`;

function BigNumber({
  title,
  data,
  theme,
  numberSize,
  labelSize,
  numberPadding,
  handleClick,
  switchAccount,
  isInteractive,
  admindashboard,
  tableData,
  tableMetadata,
  name
}) {
  const [showModal, setShowModal] = useState(false);

  const handleTableClick = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const renderTable = () => (
    <BigNumberTable
      tableData={tableData}
      tableMetadata={tableMetadata}
      switchAccount={switchAccount}
      widgetName={name}
    />
  );

  return (
    <ChartContainer>
      {title && (
        <TitleContainer>
          <Title className="title">{title}</Title>
          {admindashboard && tableData && (
            <TableButton onClick={handleTableClick} title="View detailed data">
              <Table size="sm" />
            </TableButton>
          )}
        </TitleContainer>
      )}
      <FlexContainer height="100%" width="100%" justifyContent="center">
        {Object.keys(data).map((key, idx) => (
          <NumberContainer
            key={key}
            color={idx % 2 === 0 ? theme.colors.greyXXD : theme.colors.greyXD}
            numberSize={numberSize}
            labelSize={labelSize}
            numberPadding={numberPadding}
            isInteractive={isInteractive}
            onClick={handleClick ? () => handleClick(key, switchAccount) : () => {}}
          >
            <span className="big-number">{data[key]}</span>
            <span className="number-label">{getTranslatedLabel(key)}</span>
          </NumberContainer>
        ))}
      </FlexContainer>

      {showModal && (
        <Modal
          title={tableMetadata && tableMetadata.modalTitle || 'Data Details'}
          close={handleCloseModal}
          width="60%"
          maxHeight="80vh"
          innerScroll
          padding="1em 2em 2em 2em"
        >
          {renderTable()}
        </Modal>
      )}
    </ChartContainer>
  );
}

BigNumber.propTypes = {
  title: PropTypes.string,
  data: PropTypes.object.isRequired,
  numberSize: PropTypes.number,
  labelSize: PropTypes.number,
  numberPadding: PropTypes.number,
  handleClick: PropTypes.func,
  isInteractive: PropTypes.bool,
  admindashboard: PropTypes.bool,
  tableData: PropTypes.object,
  tableMetadata: PropTypes.object,
  name: PropTypes.string
};

BigNumber.defaultProps = {
  title: null,
  numberSize: 60,
  labelSize: 16,
  numberPadding: 20,
  handleClick: null,
  isInteractive: false,
  admindashboard: false,
  tableData: null,
  tableMetadata: null,
  name: null
};

const mapDispatchToProps = dispatch => ({
  switchAccount: bindActionCreators(validateAndSwitchAccountWithCallback, dispatch)
});

export default connect(null, mapDispatchToProps)(withTheme(BigNumber));
